import React, { Component, useCallback, useState, useMemo, } from "react";
import { Alert, Button, Jumbotron } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useMainContext } from "../contexts";
import { PageHeaderProvider, PageHeaderContext, } from "../contexts/MainPageHeaderContext";
import ExtensionGate from "../controls/ExtensionGate";
import Icon from "../controls/Icon";
import { getApplication } from "../util/application";
import { didIgnoreError, isOutdated } from "../util/errorHandling";
import { genHash } from "../util/genHash";
import { log } from "../util/log";
// Backward compatibility export
export { PageHeaderContext };
// Error boundaries must be class components - no hook equivalent exists
class PageErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { error: undefined, errorInfo: undefined };
    }
    componentDidCatch(error, errorInfo) {
        this.setState({ error, errorInfo });
    }
    render() {
        const { error, errorInfo } = this.state;
        const { pageId, classes, children } = this.props;
        if (error !== undefined) {
            return (React.createElement(ErrorFallback, { classes: classes, error: error, errorInfo: errorInfo, pageId: pageId, onRetry: () => this.setState({ error: undefined, errorInfo: undefined }) }));
        }
        return children;
    }
}
const ErrorFallback = ({ pageId, classes, error, errorInfo, onRetry, }) => {
    const { t } = useTranslation(["common"]);
    const context = useMainContext();
    const report = useCallback(() => {
        context.api.events.emit("report-feedback", error.stack.split("\n")[0], `Component rendering error

Vortex Version: ${getApplication().version},

${error.stack}

ComponentStack:
  ${errorInfo.componentStack}
`, [], genHash(error));
    }, [context.api, error, errorInfo]);
    return (React.createElement("div", { className: classes.join(" "), id: `page-${pageId}` },
        React.createElement(Alert, { bsStyle: "danger", className: "render-failure" },
            React.createElement(Icon, { className: "render-failure-icon", name: "sad" }),
            React.createElement("div", { className: "render-failure-text" }, t("Failed to render.")),
            React.createElement("div", { className: "render-failure-buttons" },
                isOutdated() || didIgnoreError() ? null : (React.createElement(Button, { onClick: report }, t("Report"))),
                React.createElement(Button, { onClick: onRetry }, t("Retry"))))));
};
export const MainPageContainer = ({ page, active, secondary, }) => {
    const { t } = useTranslation(["common"]);
    const [headerRef, setHeaderRef] = useState(null);
    const classes = useMemo(() => {
        const result = ["main-page"];
        result.push(active ? "page-active" : "page-hidden");
        if (secondary) {
            result.push("secondary");
        }
        return result;
    }, [active, secondary]);
    const headerContextValue = useMemo(() => ({
        headerPortal: () => headerRef,
        page: page.id,
    }), [headerRef, page.id]);
    const handleHeaderRef = useCallback((ref) => {
        setHeaderRef(ref);
    }, []);
    // Render the page content
    let content;
    try {
        const props = page.propsFunc();
        content = (React.createElement(PageHeaderProvider, { value: headerContextValue },
            React.createElement("div", { className: classes.join(" "), id: `page-${page.id}` },
                React.createElement("div", { className: "mainpage-header-container", ref: handleHeaderRef }),
                React.createElement("div", { className: "mainpage-body-container" },
                    React.createElement(ExtensionGate, { id: page.id },
                        React.createElement(page.component, { active: active, secondary: secondary, ...props }))))));
    }
    catch (err) {
        log("warn", "error rendering extension main page", err);
        content = (React.createElement("div", { className: classes.join(" ") },
            React.createElement(Jumbotron, null,
                React.createElement("h4", null, t("Unavailable")))));
    }
    return (React.createElement(PageErrorBoundary, { classes: classes, pageId: page.id }, content));
};
export default MainPageContainer;
